import React from 'react';

/**
 * The seven discharge types KIPU reports, in the order they read best: the
 * planned endings first, the unplanned ones last.
 */
const DISCHARGE_TYPES = [
  { field: 'num_treatment_completed_full', label: 'Treatment completed in full' },
  { field: 'num_completed_episode_of_care', label: 'Completed episode of care' },
  { field: 'num_transfer_higher_level', label: 'Transfer to higher level' },
  { field: 'num_referred_outside_agency', label: 'Referred outside agency' },
  { field: 'num_evaluation_only', label: 'Evaluation only' },
  { field: 'num_treatment_drop_out', label: 'Treatment drop out' },
  { field: 'num_against_advice', label: 'Against advice' },
];

/**
 * Discharge detail for one clinician-month.
 *
 * KIPU reports a count per discharge type and a single Average Length of Stay
 * for the month — there is no per-discharge row anywhere in the feed, so the
 * stay length shown is the month's average across all of them, labelled as
 * such rather than implied to be per discharge.
 *
 * `record` is a clinician_data_records row. Records written before the
 * breakdown columns existed have NULL for all seven, which reads as "no
 * breakdown" instead of seven zeros.
 */
export default function DischargeBreakdown({ record }) {
  if (!record) return null;

  const present = DISCHARGE_TYPES
    .map(type => ({ ...type, count: record[type.field] }))
    .filter(type => type.count != null);

  const total = record.discharges ?? 0;
  const los = record.length_of_stay;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h4 className="text-sm font-bold text-text">Discharges</h4>
        <span className="text-sm font-bold text-primary">{total}</span>
      </div>

      {present.length === 0 ? (
        <p className="text-xs text-gray-400 leading-tight">
          No type breakdown for this month. KIPU began reporting discharge
          types on records synced after this one.
        </p>
      ) : present.every(t => t.count === 0) ? (
        <p className="text-xs text-gray-400 leading-tight">No discharges this month.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {present.filter(t => t.count > 0).map(type => (
            <div key={type.field} className="flex items-baseline justify-between gap-3 py-1">
              <span className="text-xs text-gray-600 leading-tight">{type.label}</span>
              <span className="text-xs font-semibold text-text">{type.count}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-2 pt-2 border-t border-gray-100 leading-tight">
        {los != null
          ? `Average length of stay ${Number(los).toFixed(0)} days across these discharges`
          : 'No length of stay reported for this month'}
      </p>
    </div>
  );
}
